import type { Session as SupabaseSession, User as SupabaseUser } from '@supabase/supabase-js';
import { type ReactNode, createContext, useCallback, useEffect, useState } from 'react';
import { setAccessToken } from '../graphql/client';
import { getSupabase, resetSupabase } from '../supabase/client';
import {
  clearEncryptionKey,
  deriveEncryptionKey,
  setEncryptionKey,
} from '../offline/crypto';
import {
  clearCredentialHash,
  storeCredentialHash,
  verifyOfflineCredential,
  isOfflineLoginValid,
} from './offline-credentials';
import { type ZooConfig, clearZooConfig, getZooConfig } from './onboarding';
import { getStoredSecurityPolicy } from './security-policy';

/** Fixed salt for encryption key derivation (not secret — just ensures deterministic key from password) */
const ENCRYPTION_SALT = new Uint8Array([
  0x7a, 0x6f, 0x6f, 0x62, 0x6f, 0x6f, 0x6b, 0x33,
  0x2d, 0x65, 0x6e, 0x63, 0x72, 0x79, 0x70, 0x74,
  0x69, 0x6f, 0x6e, 0x2d, 0x73, 0x61, 0x6c, 0x74,
  0x2d, 0x76, 0x31, 0x2d, 0x66, 0x69, 0x78, 0x65,
]);

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
}

export interface AuthContextValue {
  /** Current Supabase session (null when signed out) */
  session: SupabaseSession | null;
  /** Mapped user (null when signed out) */
  user: AuthUser | null;
  /** True while initial session is being restored */
  loading: boolean;
  /** True if onboarding (zoo discovery) has been completed */
  hasZooConfig: boolean;
  /** The stored zoo config (name, logo, etc.) */
  zooConfig: ZooConfig | null;
  /** Sign in with email + password (online) */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign in with cached credentials (offline) */
  signInOffline: (email: string, password: string) => Promise<void>;
  /** Send a magic link to the given email */
  sendMagicLink: (email: string) => Promise<void>;
  /** Send a password reset email */
  resetPassword: (email: string) => Promise<void>;
  /** Sign out and optionally clear zoo config */
  signOut: (clearZoo?: boolean) => Promise<void>;
  /** Force re-check of zoo config (after onboarding completes) */
  refreshZooConfig: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function mapUser(user: SupabaseUser | null): AuthUser | null {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email ?? '',
    fullName: user.user_metadata?.full_name ?? user.email ?? '',
    avatarUrl: user.user_metadata?.avatar_url ?? null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [zooConfig, setZooConfig] = useState<ZooConfig | null>(() => getZooConfig());

  const hasZooConfig = zooConfig !== null;

  // Initialize session from Supabase
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run when zooConfig changes (creates new Supabase client via getSupabase())
  useEffect(() => {
    const client = getSupabase();
    if (!client) {
      setLoading(false);
      return;
    }

    client.auth
      .getSession()
      .then(({ data }) => {
        setSession(data.session);
        setUser(mapUser(data.session?.user ?? null));
        // Push token to GraphQL client so fetchOptions can use it synchronously
        setAccessToken(data.session?.access_token ?? null);
      })
      .catch((err) => {
        // Invalid refresh token (e.g. staging with stale session) — clear and show login
        console.warn('Session restore failed, clearing state:', err.message);
        setSession(null);
        setUser(null);
        setAccessToken(null);
      })
      .finally(() => setLoading(false));

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(mapUser(newSession?.user ?? null));
      // Keep GraphQL client token in sync with auth state
      setAccessToken(newSession?.access_token ?? null);
    });

    return () => subscription.unsubscribe();
  }, [zooConfig]);

  const signIn = useCallback(async (email: string, password: string) => {
    const client = getSupabase();
    if (!client) throw new Error('Supabase not configured — complete onboarding first');
    const { data, error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Cache credential hash for offline login + derive encryption key
    if (data.user) {
      storeCredentialHash(email, password, data.user.id).catch((err) =>
        console.warn('[Auth] Failed to cache credential hash:', err),
      );
      deriveEncryptionKey(password, ENCRYPTION_SALT)
        .then(setEncryptionKey)
        .catch((err) => console.warn('[Auth] Failed to derive encryption key:', err));
    }
  }, []);

  const signInOffline = useCallback(async (email: string, password: string) => {
    const policy = getStoredSecurityPolicy();
    if (!policy.offlineLoginEnabled) {
      throw new Error('Offline prihlaseni neni povoleno');
    }
    const valid = await isOfflineLoginValid(email, policy.offlineLoginMaxAgeHours);
    if (!valid) {
      throw new Error('Ulozene prihlasovaci udaje vyprsely — pripojte se k internetu');
    }
    const userId = await verifyOfflineCredential(email, password);
    if (!userId) {
      throw new Error('Neplatne heslo');
    }
    // Derive encryption key for accessing encrypted cached data
    deriveEncryptionKey(password, ENCRYPTION_SALT)
      .then(setEncryptionKey)
      .catch((err) => console.warn('[Auth] Failed to derive encryption key (offline):', err));
    // Create a minimal offline session marker (no JWT — read-only access to cached data)
    setSession({
      access_token: '',
      refresh_token: '',
      expires_in: 0,
      expires_at: 0,
      token_type: 'bearer',
      user: {
        id: userId,
        email,
        app_metadata: {},
        user_metadata: {},
        aud: 'authenticated',
        created_at: new Date().toISOString(),
      },
    } as SupabaseSession);
    setUser({ id: userId, email, fullName: email, avatarUrl: null });
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    const client = getSupabase();
    if (!client) throw new Error('Supabase not configured — complete onboarding first');
    const { error } = await client.auth.signInWithOtp({ email });
    if (error) throw error;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const client = getSupabase();
    if (!client) throw new Error('Supabase not configured — complete onboarding first');
    const { error } = await client.auth.resetPasswordForEmail(email);
    if (error) throw error;
  }, []);

  const signOut = useCallback(async (clearZoo = false) => {
    const client = getSupabase();
    // Clear cached credential hash + encryption key on explicit sign-out
    clearEncryptionKey();
    const currentEmail = user?.email;
    if (currentEmail) {
      clearCredentialHash(currentEmail).catch(() => {});
    }
    if (client) {
      const { error } = await client.auth.signOut();
      if (error) throw error;
    }
    // Clear cached token from GraphQL client
    setAccessToken(null);
    if (clearZoo) {
      clearZooConfig();
      resetSupabase();
      setZooConfig(null);
      setSession(null);
      setUser(null);
    }
  }, [user?.email]);

  const refreshZooConfig = useCallback(() => {
    resetSupabase();
    setZooConfig(getZooConfig());
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        hasZooConfig,
        zooConfig,
        signIn,
        signInOffline,
        sendMagicLink,
        resetPassword,
        signOut,
        refreshZooConfig,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
